import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            setIsError(true);
            setMessage("Invalid or missing reset token.");
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setIsError(true);
            setMessage("Passwords do not match.");
            return;
        }

        if (newPassword.length < 6) {
            setIsError(true);
            setMessage("Password must be at least 6 characters long.");
            return;
        }

        setIsLoading(true);
        setMessage('');
        setIsError(false);

        try {
            const response = await fetch('https://healthcard.searchisend.com/health_api/reset_password.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token, newPassword }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to reset password.');
            }

            setMessage(result.message);
            setIsError(false);
            setIsSuccess(true);

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);

        } catch (error) {
            setMessage(error.message);
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    };

    if (!token && !message) {
        return null; // or a loading spinner
    }

    return (
        <div className="glass p-8 md:p-10 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] w-full max-w-md relative">
            <h2 className="text-4xl font-extrabold text-brand-secondary text-center mb-4 tracking-tight">Set New Password</h2>

            {isSuccess ? (
                <div className="text-center">
                    <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner border-4 border-white">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                    </div>
                    <p className="text-emerald-700 font-bold text-lg mb-2">Password Reset Successful!</p>
                    <p className="text-slate-500 font-medium">Redirecting you to login...</p>
                </div>
            ) : (
                <>
                    <p className="text-center text-slate-500 font-medium mb-8">
                        Please enter your new password below.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">New Password</label>
                            <input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password"
                                required
                                disabled={!token}
                                className="w-full p-4 bg-white/60 text-brand-secondary rounded-2xl border border-white/40 focus:outline-none focus:ring-2 focus:ring-brand-primary placeholder-slate-400 font-medium transition-shadow focus:bg-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-brand-secondary mb-1.5 ml-1">Confirm New Password</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                required
                                disabled={!token}
                                className="w-full p-4 bg-white/60 text-brand-secondary rounded-2xl border border-white/40 focus:outline-none focus:ring-2 focus:ring-brand-primary placeholder-slate-400 font-medium transition-shadow focus:bg-white"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading || !token}
                            className="w-full p-4 mt-2 bg-brand-primary hover:bg-emerald-700 rounded-full font-bold text-white transition-all transform hover:-translate-y-1 shadow-lg shadow-emerald-700/30 disabled:bg-slate-300 disabled:transform-none disabled:shadow-none text-lg"
                        >
                            {isLoading ? 'Resetting Password...' : 'Reset Password'}
                        </button>

                        {message && (
                            <div className={`mt-6 text-center p-4 rounded-xl font-medium border ${isError ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-emerald-700 border-emerald-200'}`}>
                                {message}
                            </div>
                        )}
                    </form>
                </>
            )}
        </div>
    );
}

export default ResetPassword;
